import React, {useState, useContext, useEffect} from 'react';
import AppHeader from './AppHeader';
import GUIInstrumentGridOptionsModal from "./marketwatch/GUIInstrumentGridOptionsModal";
import {DataContext} from './contexts/DataContext';
import MarketWatch from "./marketwatch/MarketWatch";
import "./css/Dashboard.css";
import SuperStarInstrumentsGrid from "./superstar/SuperStarInstrumentsGrid";
import StockTracking from "@dashboard/stocktracking/StockTracking";
import {useSharingContext} from './contexts/SharingContext';
import StagnantInstrumentsGrid from "./stagnant/StagnantInstrumentsGrid";
import PortfolioGrid from "./portfolio/PortfolioGrid";
import PortfolioPage from "./portfolio/PortfolioPage";
import ChartDrawer from './charts/ChartDrawer';
import ScreenerAnnouncement from "@dashboard/screenerannouncements/ScreenerAnnouncement";
import {ScreenerAnnouncementFeedProvider} from "@dashboard/screenerannouncements/context/ScreenerAnnContext";
// NEW: ResultsDates component
import ResultDates from "@dashboard/resultdates/ResultDates";
import {ResultDatesProvider} from "@dashboard/resultdates/context/ResultDatesContext";
import ScreenerResultsDisplay from "@dashboard/screenerresultsdisplay/ScreenerResultsDisplay";
import {ScreenerResultsDisplayProvider} from "@dashboard/screenerresultsdisplay/context/ScreenerResultsDisplayContext";
import MonitoringDashboard from "../Monitoring/MonitoringDashboard";
import PositionsGrid from "@dashboard/positions/PositionsGrid";
import AdvancedOrdersPage from "@dashboard/advancedorders/AdvancedOrdersPage";
import {LoginContext} from "../../LoginContext";
import ImportContractNote from "@portfolio/importcnote/ImportContractNote";
import AlertDisplay from "@dashboard/alerts/CNoteAlert/AlertDisplay";
import PriceVolumeAlertPanel from "@dashboard/pricevolumealerts/PriceVolumeAlertPanel";
import SharpMonitoringGrid from "@dashboard/sharpmonitoring/SharpMonitoringGrid";
import PriceVolumeAlertsManager from "@dashboard/pricevolumealerts/PriceVolumeAlertsManager";
import ManageRemindersPopup from "@dashboard/reminders/ManageRemindersPopup";
import SuperStarGrid from "@superstar/SuperStarGrid";
import UserManagementPage from "@components/UserManagement/UserManagementPage";
import ActiveZerodhaOrdersPanel from "@dashboard/activeorders/ActiveZerodhaOrdersPanel";
import {useLocation, useNavigate} from "react-router-dom";

const Dashboard = ({handleLogin}) => {
    const {tabList, handleLoginSuccess, isDataContextLoaded} = useContext(DataContext);
    const [showOptionsModal, setShowOptionsModal] = useState(false);

    // This is the single source of truth for which main view is shown
    const [visibleComponent, setVisibleComponent] = useState('PF');

    const {currentlyHeldPositions, SharingContextLoading} = useSharingContext();
    const [selectedToken, setSelectedToken] = useState(null);
    const [selectedChartSymbol, setSelectedChartSymbol] = useState(null);
    const { zerodhaConnectionStatusDTO, handleKiteLogin, handleKiteLoginForUser } = useContext(LoginContext);
    const [showPendingCnoteAlert, setShowPendingCnoteAlert] = useState(false);
    const [importNote, setImportNote] = useState(null);
    const [pendingCnoteRefreshVersion, setPendingCnoteRefreshVersion] = useState(0);
    const [manageAlertsOpen, setManageAlertsOpen] = useState(false);
    const missingRequiredUsers = zerodhaConnectionStatusDTO?.missingRequiredZerodhaUserIds || [];
    const [remindersOpen, setRemindersOpen] = useState(false);
    const location = useLocation();
    const [aoHighlightOrderId, setAoHighlightOrderId] = useState(null);

// 🔹 Decide what message to show
    const zerodhaMessage = (() => {
        if (missingRequiredUsers.length > 0) {
            return `Login to Zerodha for: ${missingRequiredUsers.join(", ")}`;
        }
        if (!zerodhaConnectionStatusDTO?.readyForTrading) {
            return "Zerodha not ready";
        }

        return null;
    })();

    const handleManageRemindersClick = () => {
        setRemindersOpen(true);
    };
    const navigate = useNavigate();
    const handleComponentSwitch = (component) => {
        //console.log("Header switching to:", component);

        // keep highlightOrderId only for AO; otherwise clear it
        const sp = new URLSearchParams(location.search);
        sp.set("vc", component);
        if (component !== "AO") sp.delete("highlightOrderId");

        navigate(
            { pathname: location.pathname, search: `?${sp.toString()}` },
            { replace: true }
        );
    };


    const ActiveComponent = React.useMemo(() => {
        switch (visibleComponent) {
            case 'MW':
                /*if (!kiteTradingReady) {
                    return (
                        <div className="dashboard-kite-locked">
                            MarketWatch is unavailable: Zerodha is not connected or not ready.
                        </div>
                    );
                }*/
                return (
                    <MarketWatch
                        tabList={tabList}
                        isDataContextLoaded={isDataContextLoaded}
                        setSelectedToken={setSelectedToken}
                        setSelectedChartSymbol={setSelectedChartSymbol}
                    />
                );
            case 'PF':
                return <PortfolioPage/>;
            case 'ST':
                return (
                    <div className="st-two-column">
                        <div className="st-left-pane">
                            <StockTracking />
                        </div>

                       {/* <div className="st-right-pane">
                            <PriceVolumeAlertPanel />
                        </div>*/}
                    </div>
                );
            case 'SS':
                return <SuperStarGrid/>;
            case 'SC':
                return <StagnantInstrumentsGrid/>;
            case 'SM':
                return <SharpMonitoringGrid/>;
            case 'ANN':
                return (
                    <ScreenerAnnouncementFeedProvider>
                        <ScreenerAnnouncement/>
                    </ScreenerAnnouncementFeedProvider>
                );
            case 'RES':
                return (
                    <ResultDatesProvider>
                        <ResultDates/>
                    </ResultDatesProvider>
                );
            case 'ORD':
                return <ActiveZerodhaOrdersPanel />;
            case 'POS':
                return <PositionsGrid/>;
            case 'SR':
                return (
                    <ScreenerResultsDisplayProvider>
                        <ScreenerResultsDisplay/>
                    </ScreenerResultsDisplayProvider>
                );
            case 'AO':
                return <AdvancedOrdersPage highlightOrderId={aoHighlightOrderId} />;
            case 'MON':
                return <MonitoringDashboard/>;
            case 'UM':
                return <UserManagementPage />;
            default:
                return null;
        }
    }, [visibleComponent, tabList, isDataContextLoaded, aoHighlightOrderId]);

    useEffect(() => {
        //console.log("Dashboard location.search changed:", location.search);
    }, [location.search]);

    useEffect(() => {
        const sp = new URLSearchParams(location.search);

        const vc = sp.get("vc"); // expecting "AO"
        const rawId = sp.get("highlightOrderId");
        const id = rawId ? Number(rawId) : null;

        /*if (vc) setVisibleComponent(vc);*/
        setVisibleComponent(vc || "PF");

        if (Number.isFinite(id)) setAoHighlightOrderId(id);
        else setAoHighlightOrderId(null);
    }, [location.search]);

    if (SharingContextLoading || !isDataContextLoaded) {
        return (
            <div className="at-loading-screen">
                <div className="at-loading-card">
                    <div className="at-spinner"></div>

                    <div className="at-loading-title">Initializing Terminal…</div>
                    <div className="at-loading-subtitle">
                        Fetching market data, portfolio, results, configs…
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="parent-container">
            {/* Header now receives the active component key so tab highlight and content are always in sync */}
            <AppHeader
                className="header"
                activeComponentKey={visibleComponent}
                onComponentSwitch={handleComponentSwitch}
                onPendingContractNotesClick={() => setShowPendingCnoteAlert(true)}
                onManageAlertsClick={() => setManageAlertsOpen(true)}
                onManageRemindersClick={handleManageRemindersClick} // NEW
            />

            {zerodhaMessage && (
                <div className="dashboard-zerodha-banner">
                    <span>{zerodhaMessage}</span>

                    {missingRequiredUsers.length > 0 ? (
                        <div className="dashboard-zerodha-banner-buttons">
                            {missingRequiredUsers.map(userId => (
                                <button
                                    key={userId}
                                    onClick={() => handleKiteLoginForUser(userId)}
                                >
                                    Login {userId}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            )}


            <div className="content-container">
                <div className="content-holder">
                    {ActiveComponent}
                </div>
                <ChartDrawer
                    visible={!!selectedToken}
                    token={selectedToken}
                    symbol={selectedChartSymbol}
                    onClose={() => setSelectedToken(null)}
                />
            </div>

            <AlertDisplay
                isOpen={showPendingCnoteAlert}
                onClose={() => setShowPendingCnoteAlert(false)}
                onImport={(note) => {
                    setImportNote(note);
                }}
                refreshVersion={pendingCnoteRefreshVersion}
            />

            {importNote && (
                <ImportContractNote
                    downloadedNoteId={importNote.id}
                    onClose={() => setImportNote(null)}
                    onSuccess={() => {
                        setPendingCnoteRefreshVersion(v => v + 1);
                    }}
                />
            )}
            <PriceVolumeAlertsManager
                open={manageAlertsOpen}
                onClose={() => setManageAlertsOpen(false)}
            />

            <ManageRemindersPopup
                open={remindersOpen}
                onClose={() => setRemindersOpen(false)}
            />
        </div>
    );
};

export default Dashboard;
